<template>
  <div>
    <v-card-subtitle class="py-0">Itinerary details</v-card-subtitle>
    <v-simple-table dense>
      <template v-slot:default>
        <tbody class="itinerary_details">
          <tr>
            <td>Traveller</td>
            <td>
              {{ BOOKING.firstName }} {{ BOOKING.lastName }} <br />
              <div v-if="BOOKING.mobileNumber">mob: {{ BOOKING.mobileNumber }}<br /></div>
              <div v-if="BOOKING.phoneNumber">ph: {{ BOOKING.phoneNumber }}</div>
            </td>
          </tr>
          <tr>
            <td>Email</td>
            <td>{{ BOOKING.emailAddress }}</td>
          </tr>
          <template v-if="IS_ADMINISTRATOR && (!!bookingCharges && bookingCharges.length > 0)">
            <tr>
              <td>Booking charges</td>
              <td>
                <span v-for="item in bookingCharges" :key="item.id">
                  {{ item.price | toCurrency }}: {{item.description}} <br />
                </span>
              </td>
            </tr>
          </template>
          <tr>
            <td>Number of passengers (including children)</td>
            <td>{{ BOOKING.passengers }}</td>
          </tr>
          <tr v-if="BOOKING.luggage">
            <td>Excess luggage</td>
            <td>{{ BOOKING.luggage }}</td>
          </tr>
          <tr v-if="BOOKING.surfboards">
            <td>Surfboards</td>
            <td>{{ BOOKING.surfboards }}</td>
          </tr>
          <tr v-if="BOOKING.bicycles">
            <td>Bicycles</td>
            <td>{{ BOOKING.bicycles }}</td>
          </tr>
          <tr v-if="BOOKING.golfClubs">
            <td>Golf clubs</td>
            <td>{{ BOOKING.golfClubs }}</td>
          </tr>
          <tr v-if="BOOKING.boardBags">
            <td>Board bags</td>
            <td>{{ BOOKING.boardBags }}</td>
          </tr>
          <tr v-if="BOOKING.skisSnowboards">
            <td>Skis/Snowboards</td>
            <td>{{ BOOKING.skisSnowboards }}</td>
          </tr>
          <tr v-if="BOOKING.babyCapsules">
            <td>Rear facing baby seat(s)</td>
            <td>{{ BOOKING.babyCapsules }}</td>
          </tr>
          <tr v-if="BOOKING.babySeats">
            <td>Forward facing baby seat(s)</td>
            <td>{{ BOOKING.babySeats }}</td>
          </tr>
          <tr v-if="BOOKING.boosterSeats">
            <td>Booster seat(s)</td>
            <td>{{ BOOKING.boosterSeats }}</td>
          </tr>
          <tr>
            <td>Direction</td>
            <td>{{ getDirection() }}</td>
          </tr>
          <tr>
            <td>Trip type</td>
            <td>{{ getTripType() }}</td>
          </tr>
          <tr>
            <td>Pickup / drop-off address</td>
            <td>{{ getAddressText() }}</td>
          </tr>
          <template v-if="BOOKING.direction == 1 || BOOKING.direction == 3">
            <tr>
              <td>Pickup time from address</td>
              <td>{{ getPickupDateTime(BOOKING.pickUpDetails) }}</td>
            </tr>
            <tr>
              <td>Departing flight</td>
              <td>{{ getFlightDescription(BOOKING.pickUpDetails.departingFlightDetails,
                BOOKING.pickUpDetails.travelDate,
                'departs') }}; Arriving:
                {{ BOOKING.pickUpDetails.departingFlightDetails.otherAirport }}</td>
            </tr>
          </template>
          <template v-if="BOOKING.direction == 2 || BOOKING.direction == 3">
              <tr>
                <td>Arriving flight</td>
                <td>{{ getFlightDescription(BOOKING.dropOffDetails.arrivingFlightDetails,
                  BOOKING.dropOffDetails.travelDate,
                  'arrives') }}; Departing:
                  {{ BOOKING.dropOffDetails.arrivingFlightDetails.otherAirport }}</td>
              </tr>
          </template>
        </tbody>
      </template>
    </v-simple-table>
    <ValidationProvider rules="required" v-slot="{ errors }" v-if="showDisclaimer">
      <div>
        <v-checkbox
          v-model="termsConditionsAgreed"
          label=""
          :error-messages="errors"
          color="primary"
          value="true"
          hide-details>
          <template v-slot:label>
            <div>
              I have read and Agree to the
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <a
                    target="_blank"
                    href="https://asntransfers.com/Home.html"
                    @click.stop
                    v-on="on">
                    Terms and Conditions.
                  </a>
                </template>
                Opens Terms and Conditions in new window.
              </v-tooltip>
            </div>
          </template>
        </v-checkbox>
      </div>
    </ValidationProvider>
  </div>
</template>

<script>
import { required } from 'vee-validate/dist/rules';
import { mapFields } from 'vuex-map-fields';
import { mapGetters } from 'vuex';

import
{
  extend,
  ValidationProvider,
  setInteractionMode,
} from 'vee-validate';
import storeConstants from '@/store/storeConstants';

setInteractionMode('eager');
extend('required', required);

export default {
  name: 'Itinerary',
  components: {
    ValidationProvider,
  },
  props: {
    showDisclaimer: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    termsConditionsAgreed: undefined,
  }),
  computed: {
    ...mapGetters([storeConstants.BOOKING,
      storeConstants.SHORT_DATE_FORMAT,
      storeConstants.IS_ADMINISTRATOR]),
    ...mapFields([
      'booking.bookingCharges',
    ]),
  },
  methods: {
    getAddressText() {
      return this.BOOKING.selectedAddress.formattedAddress;
    },
    getTripType() {
      if (this.BOOKING.exclusiveCharter) {
        return 'Exclusive charter';
      }
      return 'Shared ride';
    },
    getDirection() {
      if (this.BOOKING.direction === 1) {
        return 'To airport (Pick-up)';
      }
      if (this.BOOKING.direction === 2) {
        return 'From airport (Drop-off)';
      }
      if (this.BOOKING.direction === 3) {
        return 'Return trip (Pick-up/drop-off)';
      }
      return null;
    },
    getFlightDescription(flightDetails, travelDate, direction) {
      if (flightDetails) {
        return `${flightDetails.carrierName} ${flightDetails.flightNumber}: ${direction}: ${this.$moment(travelDate).format(this.SHORT_DATE_FORMAT)}
        ${flightDetails.flightTime}`;
      }
      return '';
    },
    getPickupDateTime(pickupDetails) {
      return `${this.$moment(pickupDetails.travelDate).format(this.SHORT_DATE_FORMAT)} : 
      ${this.$moment(pickupDetails.pickupTime, 'HH:mm').format('HH:mm')} - 
      ${this.$moment(pickupDetails.pickupTime, 'HH:mm').add(15, 'minutes').format('HH:mm')}`;
    },
  },
};
</script>
